/**
 * Native App Initialization Service
 * 
 * Handles native mobile app setup including:
 * - Splash screen
 * - Status bar
 * - Keyboard behavior
 * - App lifecycle
 */

import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { App } from '@capacitor/app';

class NativeAppService {
  static isNative = Capacitor.isNativePlatform();
  static platform = Capacitor.getPlatform();

  /**
   * Initialize the native app
   * Call this in App.js or index.js
   */
  static async initialize() {
    if (!this.isNative) {
      console.log('[NativeApp] Running in web mode');
      return;
    }

    console.log(`[NativeApp] Initializing on ${this.platform}`);

    try {
      // Configure status bar
      await this.configureStatusBar();

      // Configure keyboard
      await this.configureKeyboard();

      // Set up app lifecycle listeners
      this.setupAppLifecycle();

      // Initialize notification service
      await this.initNotifications();

      // Hide splash screen after a short delay
      setTimeout(async () => {
        await SplashScreen.hide({
          fadeOutDuration: 300
        });
        console.log('[NativeApp] Splash screen hidden');
      }, 1000);

    } catch (error) {
      console.error('[NativeApp] Initialization error:', error);
    }
  }

  /**
   * Initialize notification service
   */
  static async initNotifications() {
    try {
      const { initNotificationService } = await import('@/services/nativeNotificationService');
      await initNotificationService();
      console.log('[NativeApp] Notification service initialized');
    } catch (error) {
      console.warn('[NativeApp] Notification service init error:', error);
    }
  }

  /**
   * Configure the status bar
   */
  static async configureStatusBar() {
    try {
      // Set status bar style
      await StatusBar.setStyle({ style: Style.Light });
      
      // Set background color (matches app theme)
      if (this.platform === 'android') {
        await StatusBar.setBackgroundColor({ color: '#F0FDF4' });
      }

      // Make status bar overlay (iOS)
      await StatusBar.setOverlaysWebView({ overlay: false });

      console.log('[NativeApp] Status bar configured');
    } catch (error) {
      console.warn('[NativeApp] Status bar config error:', error);
    }
  }

  /**
   * Configure keyboard behavior
   */
  static async configureKeyboard() {
    try {
      // Set keyboard resize mode
      await Keyboard.setResizeMode({ mode: KeyboardResize.Body });

      // Keyboard event listeners
      Keyboard.addListener('keyboardWillShow', (info) => {
        console.log('[NativeApp] Keyboard will show, height:', info.keyboardHeight);
        document.body.classList.add('keyboard-open');
      });

      Keyboard.addListener('keyboardWillHide', () => {
        console.log('[NativeApp] Keyboard will hide');
        document.body.classList.remove('keyboard-open');
      });

      console.log('[NativeApp] Keyboard configured');
    } catch (error) {
      console.warn('[NativeApp] Keyboard config error:', error);
    }
  }

  /**
   * Set up app lifecycle listeners
   */
  static setupAppLifecycle() {
    // App state change (foreground/background)
    App.addListener('appStateChange', ({ isActive }) => {
      console.log(`[NativeApp] App state: ${isActive ? 'active' : 'background'}`);
      
      if (isActive) {
        // App came to foreground
        document.dispatchEvent(new CustomEvent('app:resume'));
      } else {
        // App went to background
        document.dispatchEvent(new CustomEvent('app:pause'));
      }
    });

    // Handle back button (Android)
    App.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        // Optionally show exit confirmation
        // For now, minimize the app
        App.minimizeApp();
      }
    });

    // Deep links
    App.addListener('appUrlOpen', (data) => {
      console.log('[NativeApp] Deep link:', data.url);
      document.dispatchEvent(new CustomEvent('app:deeplink', { detail: data }));
    });

    console.log('[NativeApp] Lifecycle listeners configured');
  }

  /**
   * Show splash screen (useful for re-authentication)
   */
  static async showSplash() {
    if (!this.isNative) return;
    
    try {
      await SplashScreen.show({
        showDuration: 2000,
        autoHide: true,
        fadeInDuration: 300,
        fadeOutDuration: 300
      });
    } catch (error) {
      console.warn('[NativeApp] Show splash error:', error);
    }
  }

  /**
   * Hide splash screen
   */
  static async hideSplash() {
    if (!this.isNative) return;
    
    try {
      await SplashScreen.hide({ fadeOutDuration: 300 });
    } catch (error) {
      console.warn('[NativeApp] Hide splash error:', error);
    }
  }

  /**
   * Set status bar color dynamically
   */
  static async setStatusBarColor(color, style = 'Light') {
    if (!this.isNative) return;
    
    try {
      await StatusBar.setStyle({ style: style === 'Light' ? Style.Light : Style.Dark });
      
      if (this.platform === 'android') {
        await StatusBar.setBackgroundColor({ color });
      }
    } catch (error) {
      console.warn('[NativeApp] Set status bar color error:', error);
    }
  }

  /**
   * Get app info
   */
  static async getAppInfo() {
    if (!this.isNative) {
      return {
        name: 'Forget Me Not',
        id: 'com.everydayworks.forgetmenot',
        build: 'web',
        version: '1.0.0'
      };
    }

    try {
      const info = await App.getInfo();
      return info;
    } catch (error) {
      console.warn('[NativeApp] Get app info error:', error);
      return null;
    }
  }

  /**
   * Exit the app (Android only)
   */
  static async exitApp() {
    if (this.platform === 'android') {
      await App.exitApp();
    }
  }

  /**
   * Minimize the app (Android only)
   */
  static async minimizeApp() {
    if (this.platform === 'android') {
      await App.minimizeApp();
    }
  }
}

export default NativeAppService;
